/**
 * חבילות נופש LDS — מיפוי ל־UnifiedPricingInput ו־runUnifiedBookingPricing
 * (עם legacyAppRound2 כמו round2 ב־AppContext).
 *
 * ערוצים נעולים: "agent_site" | "ai_chat" — applyAgentCommission לפי channel default
 * (כרגע 0); שולי המכירה המאוחדים ב-hrFlatPercent (selectedHrPercent), תואם calcBaseCommission.
 * צ'אט לקוח: מעבירים channelType "ai_chat" (למשל דרך getLdsPackagePricingChannelType()).
 */

/** @param {unknown} v */
export function normalizeLdsPackageChannelType(v) {
  return v === "ai_chat" ? "ai_chat" : "agent_site";
}

import {
  legacyAppRound2,
  runAppContextBaseCommissionPricing,
  runUnifiedBookingPricing,
} from "./pricingEngine.js";
import { runPaymentCreditClearingOnBase } from "./paymentInstallmentsPricing.js";
import {
  flightHrCommissionMinAmount,
  resolveFlightHrDirectionCount,
  shouldApplyFlightHrCommissionMinFloor,
} from "./flightHrCommission.js";

/** נטו במטבע הספק אחרי עמלת LDS באחוזים (תואם calculateNetPrice ב־SearchContext). */
export function computeLdsNetForeignGross(gross, commissionPercent) {
  const numPrice = Number(gross) || 0;
  const numCommission = Number(commissionPercent) || 0;
  if (!numPrice) return 0;
  return legacyAppRound2(
    numPrice ? numPrice - (numPrice * numCommission) / 100 : 0
  );
}

/**
 * HR margin לתמחור LDS: מ־hrMarginPercentOverride (פיצ'רי מטריצה), לא מ־commission_percentage / wallet.
 */
export function resolveLdsAppCommissionPercents(p) {
  const isHotel = Boolean(p.isHotelDeal);
  let hrCommissionPercent = p.hrCommissionPercentOverride;
  if (!hrCommissionPercent && hrCommissionPercent !== 0) {
    if (p.isB2CMode) hrCommissionPercent = 15;
    else if (isHotel) hrCommissionPercent = p.getHrCommissionPercentHotels();
    else hrCommissionPercent = p.getHrCommissionPercent();
  }

  let agentCommissionPercent = p.agentCommissionPercentOverride;
  if (!agentCommissionPercent && agentCommissionPercent !== 0) {
    agentCommissionPercent = p.getAgentCommissionPercent();
  }

  const hrOv = p.hrMarginPercentOverride;
  const selectedHrPercent =
    hrOv != null && Number.isFinite(Number(hrOv))
      ? Number(hrOv)
      : Number(agentCommissionPercent) > 0
        ? Number(agentCommissionPercent)
        : Number(hrCommissionPercent);

  return { hrCommissionPercent, agentCommissionPercent, selectedHrPercent };
}

/**
 * Flight-only $30/pax HR floor, in ILS and in supplier (orig) currency.
 * Hotel / package / organized-tour calls leave apply=false.
 */
export function resolveFlightHrFloorForLdsParams(params = {}) {
  const meta = params?.pricingAuditMeta || {};
  const apply = shouldApplyFlightHrCommissionMinFloor({
    isHotelDeal: params.isHotelDeal === true,
    ldsHrProductType: meta.ldsHrProductType,
    enforceFlightHrCommissionSsot: meta.enforceFlightHrCommissionSsot,
  });
  if (!apply) {
    return {
      apply: false,
      passengersCount: 1,
      directionCount: 1,
      minAmountIls: 0,
      minAmountOrig: 0,
      usdToOrigRate: 1,
      usdToIlsRate: 0,
    };
  }
  const passengersCount = Math.max(1, Math.floor(Number(meta.passengersCount) || 1));
  const directionCount = resolveFlightHrDirectionCount(meta);
  const dealRate = Number(params.currencyRate);
  const usdRateRaw = Number(meta.usdCurrencyRate);
  const usdToIlsRate =
    Number.isFinite(usdRateRaw) && usdRateRaw > 0
      ? usdRateRaw
      : Number.isFinite(dealRate) && dealRate > 0
        ? dealRate
        : 0;
  const dealCode = String(meta.dealCurrencyCode || "").trim().toUpperCase();
  const usdToOrigRate =
    dealCode === "USD" || !usdToIlsRate || !(Number.isFinite(dealRate) && dealRate > 0)
      ? 1
      : usdToIlsRate / dealRate;
  return {
    apply: true,
    passengersCount,
    directionCount,
    minAmountIls: flightHrCommissionMinAmount({
      passengersCount,
      directionCount,
      usdToCurrencyRate: usdToIlsRate || 1,
    }),
    minAmountOrig: flightHrCommissionMinAmount({
      passengersCount,
      directionCount,
      usdToCurrencyRate: usdToOrigRate,
    }),
    usdToOrigRate,
    usdToIlsRate,
  };
}

/**
 * בונה UnifiedPricingInput: בסיס ILS אחרי נטו LDS והמרה; שכבת HR יחידה דרך hrFlatPercent = selectedHrPercent, ללא שכבת agent נפרדת (כמו calcBaseCommission).
 */
export function buildVacationPackagePricingInput(params) {
  const validGross = Number(params.ldsGrossForeign) || 0;
  const rate = Number(params.currencyRate);
  if (!rate || !Number.isFinite(rate)) {
    return { ok: false, reason: "invalid_rate", unifiedInput: null, meta: null };
  }

  const net = computeLdsNetForeignGross(
    validGross,
    params.ldsCommissionPercent
  );
  const priceIls = legacyAppRound2(net * rate);

  const perc = resolveLdsAppCommissionPercents(params);
  const channelType = normalizeLdsPackageChannelType(params.channelType);
  const flightHrFloor = resolveFlightHrFloorForLdsParams(params);

  const unifiedInput = {
    channelType,
    supplierGrossAmount: priceIls,
    supplierCurrency: "ILS",
    supplierCommissionType: "NET",
    hrFlatPercent: perc.selectedHrPercent,
    ...(flightHrFloor.apply
      ? { hrMinAmount: flightHrFloor.minAmountIls }
      : {}),
    /** ערוץ נעול: ברירת מחדל 0 — השולי המאוחד כבר ב-hrFlatPercent (כמו calcBaseCommission). */
    channelDefaultAgentPercent: 0,
    installmentsFeePercent: params.getCreditCardFeePercent(),
    settlementCurrency: "ILS",
    displayCurrency: "ILS",
    hotelFinalChargeInteger: false,
    roundMoney: legacyAppRound2,
  };

  return {
    ok: true,
    unifiedInput,
    meta: {
      netForeign: net,
      priceIls,
      channelType,
      ...perc,
    },
  };
}

/**
 * מחשב ourTotalFare (ILS) + origCurrency (מטבע ספק) — מנוע אחיד ל־ILS, runAppContextBaseCommissionPricing ל־origCurrency (כמו calcBaseCommission(net)).
 */
export function runLdsVacationPackageFareFromEngine(params) {
  const auditSteps = ["lds_vacation:fare:enter"];
  const validGross = Number(params.ldsGrossForeign) || 0;
  const validRate = Number(params.currencyRate) || 0;
  console.log("[FLIGHT_PRICING_ENGINE_ENTRY_DEBUG]", {
    dealId: params?.pricingAuditMeta?.dealId ?? null,
    searchTab: params?.pricingAuditMeta?.searchTab ?? null,
    context: params?.pricingAuditMeta?.pricingContext ?? null,
    channelType: params?.channelType ?? null,
  });

  if (!validRate || !Number.isFinite(validRate)) {
    auditSteps.push("reject:invalid_rate");
    return {
      pricingStatus: "missing_price",
      ourPrice: null,
      origCurrencyVm: null,
      netForeign: null,
      auditSteps,
    };
  }

  if (!validGross) {
    const ch = normalizeLdsPackageChannelType(params.channelType);
    const perc = resolveLdsAppCommissionPercents(params);
    auditSteps.push("lds:zero_gross");
    auditSteps.push(`lds_package:channel:${ch}`);
    if (ch === "ai_chat") {
      auditSteps.push("ai_chat:final_price_only");
    }
    const ourPrice = {
      initialPrice: 0,
      hrCommissionPercent: perc.hrCommissionPercent,
      agentCommissionPercent: perc.agentCommissionPercent,
      unifiedHrMarginPercent: perc.selectedHrPercent,
      unifiedHrMarginSourceFeatureKey:
        params.hrMarginSourceFeatureKey != null &&
        String(params.hrMarginSourceFeatureKey).trim() !== ""
          ? String(params.hrMarginSourceFeatureKey)
          : null,
      walletCommissionPercent:
        params.walletCommissionPercent != null &&
        Number.isFinite(Number(params.walletCommissionPercent))
          ? Number(params.walletCommissionPercent)
          : null,
      unifiedHrMargin: 0,
      hrCommission: 0,
      agentCommission: 0,
      handlingFee: 0,
      priceWithCommission: 0,
      creditCardFee: 0,
      pricePreFinalRound: 0,
      priceWithCommissionAndCreditCardFee: 0,
      pricingStatus: "ok",
      pricingAuditSteps: [...auditSteps, "lds_vacation:fare:ok"],
    };
    return {
      pricingStatus: "ok",
      ourPrice,
      origCurrencyVm: null,
      netForeign: 0,
      auditSteps: ourPrice.pricingAuditSteps,
    };
  }

  const built = buildVacationPackagePricingInput(params);
  if (!built.ok) {
    auditSteps.push(`reject:${built.reason}`);
    return {
      pricingStatus: "missing_price",
      ourPrice: null,
      origCurrencyVm: null,
      netForeign: null,
      auditSteps,
    };
  }

  const { meta, unifiedInput } = built;
  if (params.pricingAuditMeta && typeof params.pricingAuditMeta === "object") {
    unifiedInput.pricingAuditMeta = { ...params.pricingAuditMeta };
  }
  const flightHrFloor = resolveFlightHrFloorForLdsParams(params);
  const ch = meta.channelType ?? normalizeLdsPackageChannelType(params.channelType);
  auditSteps.push(`lds_package:channel:${ch}`);
  auditSteps.push(
    "lds_package:margin_via_hr_flat_pct:agent_commission_layer_channel_default_0"
  );
  auditSteps.push(
    `lds:net:${meta.netForeign}:ils:${meta.priceIls}:sel_hr:${meta.selectedHrPercent}`
  );
  if (flightHrFloor.apply) {
    auditSteps.push(
      `lds:flight_hr_min_floor:${flightHrFloor.passengersCount}pax:ils:${flightHrFloor.minAmountIls}:orig:${flightHrFloor.minAmountOrig}`
    );
  }

  const uni = runUnifiedBookingPricing(unifiedInput);
  auditSteps.push(...uni.audit.steps.map((s) => `unified:${s}`));

  if (uni.model.status !== "ok") {
    auditSteps.push(`reject:unified:${uni.model.status}`);
    return {
      pricingStatus: "missing_price",
      ourPrice: null,
      origCurrencyVm: null,
      netForeign: meta.netForeign,
      auditSteps,
    };
  }

  const m = uni.model;
  const initial = m.companyNetAmount;
  const pricingAuditMeta = params?.pricingAuditMeta || {};
  const isPublicSite = pricingAuditMeta?.isPublicSite === true;
  const pricingContext = isPublicSite ? "public_site" : "agent_system";
  /** סכומי מנוע מאוחד: HR = מרווח HotelsRiver; agent = שכבת סוכן (בערוץ נעול לרוב 0). */
  const hrAmount = Number(m.hrCommission?.amount) || 0;
  const agentAmount = Number(m.agentCommission?.amount) || 0;

  const ourPrice = {
    initialPrice: initial,
    hrCommissionPercent: meta.hrCommissionPercent,
    agentCommissionPercent: meta.agentCommissionPercent,
    unifiedHrMarginPercent: meta.selectedHrPercent,
    unifiedHrMarginSourceFeatureKey:
      params.hrMarginSourceFeatureKey != null &&
      String(params.hrMarginSourceFeatureKey).trim() !== ""
        ? String(params.hrMarginSourceFeatureKey)
        : null,
    walletCommissionPercent:
      params.walletCommissionPercent != null &&
      Number.isFinite(Number(params.walletCommissionPercent))
        ? Number(params.walletCommissionPercent)
        : null,
    unifiedHrMargin: hrAmount,
    hrCommission: hrAmount,
    agentCommission: agentAmount,
    handlingFee: 0,
    priceWithCommission: m.agentBaseDisplayPrice,
    creditCardFee: m.installments.amount,
    pricePreFinalRound: m.preFinalChargeAmount,
    priceWithCommissionAndCreditCardFee: m.finalChargePrice,
    pricingStatus: "ok",
    pricingAuditSteps: [],
    isPublicSite,
    pricingContext,
  };

  const origRes = runAppContextBaseCommissionPricing({
    price: meta.netForeign,
    isHotel: params.isHotelDeal,
    hotelFinalRoundOnly: false,
    isB2CMode: params.isB2CMode,
    getHrCommissionPercent: params.getHrCommissionPercent,
    getHrCommissionPercentHotels: params.getHrCommissionPercentHotels,
    getAgentCommissionPercent: params.getAgentCommissionPercent,
    getCreditCardFeePercent: params.getCreditCardFeePercent,
    hrMarginPercentOverride: params.hrMarginPercentOverride,
    hrMarginSourceFeatureKey: params.hrMarginSourceFeatureKey,
    walletCommissionPercent: params.walletCommissionPercent,
    applyFlightHrMinFloor: flightHrFloor.apply,
    passengersCount: flightHrFloor.passengersCount,
    flightHrDirectionCount: flightHrFloor.directionCount,
    flightHrMinUsdToCurrencyRate: flightHrFloor.usdToOrigRate,
  });

  let origCurrencyVm =
    origRes.status === "ok" && origRes.ourPrice ? origRes.ourPrice : null;
  const finalClientIls = Number(ourPrice?.priceWithCommissionAndCreditCardFee) || 0;
  const usdRate = Number(validRate) || 0;
  let finalUsdSource = "orig_currency_vm";
  if (
    (!origCurrencyVm ||
      !Number.isFinite(Number(origCurrencyVm?.priceWithCommissionAndCreditCardFee)) ||
      Number(origCurrencyVm?.priceWithCommissionAndCreditCardFee) <= 0) &&
    finalClientIls > 0 &&
    usdRate > 0
  ) {
    finalUsdSource = "derived_from_final_ils_rate";
    origCurrencyVm = {
      initialPrice: legacyAppRound2((Number(ourPrice.initialPrice) || 0) / usdRate),
      netPrice: legacyAppRound2((Number(meta.netForeign) || 0)),
      hrCommissionPercent: Number(ourPrice.hrCommissionPercent) || 0,
      agentCommissionPercent: Number(ourPrice.agentCommissionPercent) || 0,
      unifiedHrMarginSourceFeatureKey: ourPrice.unifiedHrMarginSourceFeatureKey ?? null,
      walletCommissionPercent: ourPrice.walletCommissionPercent ?? null,
      hrCommission: legacyAppRound2((Number(ourPrice.hrCommission) || 0) / usdRate),
      agentCommission: legacyAppRound2((Number(ourPrice.agentCommission) || 0) / usdRate),
      handlingFee: legacyAppRound2((Number(ourPrice.handlingFee) || 0) / usdRate),
      priceWithCommission: legacyAppRound2((Number(ourPrice.priceWithCommission) || 0) / usdRate),
      creditCardFee: legacyAppRound2((Number(ourPrice.creditCardFee) || 0) / usdRate),
      pricePreFinalRound: legacyAppRound2((Number(ourPrice.pricePreFinalRound) || 0) / usdRate),
      priceWithCommissionAndCreditCardFee: legacyAppRound2(finalClientIls / usdRate),
      pricingStatus: "ok",
      pricingAuditSteps: ["orig_currency:derived_from_ils_rate"],
    };
  }
  if (process.env.NODE_ENV === "development") {
    console.log("[FLIGHT_FINAL_USD_BUILD_DEBUG]", {
      finalClientIls: finalClientIls || null,
      usdRate: usdRate || null,
      finalClientUsd:
        Number(origCurrencyVm?.priceWithCommissionAndCreditCardFee) || null,
      source: finalUsdSource,
      dealId: params?.pricingAuditMeta?.dealId ?? null,
    });
  }
  const pipelinePax = Math.max(1, Number(params?.pricingAuditMeta?.passengersCount) || 1);
  const pipelineHrCommissionAmountUsd =
    Number(origCurrencyVm?.hrCommission) > 0
      ? Number(origCurrencyVm.hrCommission) / pipelinePax
      : 0;
  const pipelineFinalClientUsdPerPerson =
    Number(origCurrencyVm?.priceWithCommissionAndCreditCardFee) > 0
      ? Number(origCurrencyVm.priceWithCommissionAndCreditCardFee) / pipelinePax
      : 0;
  if (process.env.NODE_ENV === "development") {
    console.log("[FLIGHT_HR_PERCENT_PIPELINE_DEBUG]", {
      packageFeatureCommissionPercentage:
        Number.isFinite(Number(params?.pricingAuditMeta?.packageFeatureCommissionPercentage))
          ? Number(params.pricingAuditMeta.packageFeatureCommissionPercentage)
          : null,
      finalHrPercentUsed:
        Number.isFinite(Number(params?.pricingAuditMeta?.finalHrPercentUsed))
          ? Number(params.pricingAuditMeta.finalHrPercentUsed)
          : Number.isFinite(Number(meta?.selectedHrPercent))
            ? Number(meta.selectedHrPercent)
            : null,
      selectedHrPercent:
        Number.isFinite(Number(meta?.selectedHrPercent))
          ? Number(meta.selectedHrPercent)
          : null,
      hrFlatPercent:
        Number.isFinite(Number(unifiedInput?.hrFlatPercent))
          ? Number(unifiedInput.hrFlatPercent)
          : null,
      systemMarginAmountUsd: pipelineHrCommissionAmountUsd,
      hrCommissionAmountUsd: pipelineHrCommissionAmountUsd,
      finalClientUsdPerPerson: pipelineFinalClientUsdPerPerson,
      blockReason: params?.pricingAuditMeta?.blockReason ?? null,
    });
  }
  const guardAgentMarkupBefore = Number(origCurrencyVm?.agentCommission) || 0;
  if (!isPublicSite && origCurrencyVm && typeof origCurrencyVm === "object") {
    const hrBefore = Number(origCurrencyVm.hrCommission) || 0;
    origCurrencyVm = {
      ...origCurrencyVm,
      hrCommission: legacyAppRound2(hrBefore + guardAgentMarkupBefore),
      agentCommission: 0,
      agentCommissionPercent: 0,
    };
  }
  // Lock supplier-net USD on the order so order-details never reverse-FX with live admin rate.
  if (origCurrencyVm && typeof origCurrencyVm === "object") {
    const lockedNetForeign =
      Number.isFinite(Number(origCurrencyVm.netPrice)) && Number(origCurrencyVm.netPrice) > 0
        ? Number(origCurrencyVm.netPrice)
        : Number.isFinite(Number(origCurrencyVm.initialPrice)) &&
            Number(origCurrencyVm.initialPrice) > 0
          ? Number(origCurrencyVm.initialPrice)
          : legacyAppRound2(Number(meta.netForeign) || 0);
    if (Number.isFinite(lockedNetForeign) && lockedNetForeign > 0) {
      origCurrencyVm = {
        ...origCurrencyVm,
        netPrice: lockedNetForeign,
      };
    }
  }
  if (!isPublicSite) {
    ourPrice.agentCommission = 0;
    ourPrice.agentCommissionPercent = 0;
  }
  {
    console.log("[FLIGHT_AGENT_MARKUP_CONTEXT_GUARD]", {
      context: pricingContext,
      isPublicSite,
      agentMarkupBefore: guardAgentMarkupBefore,
      agentMarkupAfter: Number(origCurrencyVm?.agentCommission) || 0,
      agentId: pricingAuditMeta?.agentId ?? null,
      dealId: pricingAuditMeta?.dealId ?? null,
    });
    const hrPercentInput =
      Number.isFinite(Number(meta?.selectedHrPercent))
        ? Number(meta.selectedHrPercent)
        : Number.isFinite(Number(params?.pricingAuditMeta?.finalHrPercentUsed))
          ? Number(params.pricingAuditMeta.finalHrPercentUsed)
          : 0;
    const netUsdAfterSupplierCommissionPerPerson =
      (Number(meta?.netForeign) || 0) / pipelinePax;
    const rawHrPercentCalculation =
      netUsdAfterSupplierCommissionPerPerson * (hrPercentInput / 100);
    const targetPriceBeforeRounding =
      Number(origCurrencyVm?.pricePreFinalRound) > 0
        ? Number(origCurrencyVm.pricePreFinalRound) / pipelinePax
        : Number(origCurrencyVm?.priceWithCommissionAndCreditCardFee) > 0
          ? Number(origCurrencyVm.priceWithCommissionAndCreditCardFee) / pipelinePax
          : 0;
    const targetPriceAfterRounding =
      Number(origCurrencyVm?.priceWithCommissionAndCreditCardFee) > 0
        ? Number(origCurrencyVm.priceWithCommissionAndCreditCardFee) / pipelinePax
        : 0;
    const systemMarginAmountUsd =
      Number(origCurrencyVm?.hrCommission) > 0
        ? Number(origCurrencyVm.hrCommission) / pipelinePax
        : 0;
    const roundingDeltaUsd = targetPriceAfterRounding - targetPriceBeforeRounding;
    console.log("[FLIGHT_HR_INTERNAL_CALC_DEBUG]", {
      netUsdAfterSupplierCommission: legacyAppRound2(netUsdAfterSupplierCommissionPerPerson),
      hrPercentInput: legacyAppRound2(hrPercentInput),
      rawHrPercentCalculation: legacyAppRound2(rawHrPercentCalculation),
      targetPriceBeforeRounding: legacyAppRound2(targetPriceBeforeRounding),
      targetPriceAfterRounding: legacyAppRound2(targetPriceAfterRounding),
      systemMarginAmountUsd: legacyAppRound2(systemMarginAmountUsd),
      roundingDeltaUsd: legacyAppRound2(roundingDeltaUsd),
      dealId: pricingAuditMeta?.dealId ?? null,
      sourceFunction: "runLdsVacationPackageFareFromEngine",
    });
  }
  if (origRes.status === "ok") {
    auditSteps.push(...origRes.auditSteps.map((s) => `orig_currency:${s}`));
  } else {
    auditSteps.push("orig_currency:missing_price");
  }

  auditSteps.push("lds_vacation:fare:ok");
  if (ch === "ai_chat") {
    auditSteps.push("ai_chat:final_price_only");
  }
  ourPrice.pricingAuditSteps = [...auditSteps];
  if (
    process.env.NODE_ENV === "development" &&
    params?.pricingAuditMeta?.hrPlanAuditEnabled === true
  ) {
    const auditMeta = params.pricingAuditMeta || {};
    const pax = Math.max(1, Number(auditMeta.passengersCount) || 1);
    const apiGrossUsdPerPerson = legacyAppRound2(validGross / pax);
    const netUsdAfterSupplierCommission = legacyAppRound2((Number(meta.netForeign) || 0) / pax);
    const hrCommissionAmountUsd = legacyAppRound2(
      (Number(origCurrencyVm?.hrCommission) || 0) / pax
    );
    const finalClientUsdPerPerson = legacyAppRound2(
      (Number(origCurrencyVm?.priceWithCommissionAndCreditCardFee) || 0) / pax
    );
    console.log("[FLIGHT_AGENT_PLAN_HR_COMMISSION_DEBUG]", {
      agentId: auditMeta.agentId ?? null,
      agentEmail: auditMeta.agentEmail ?? null,
      rawPackageId: auditMeta.rawPackageId ?? null,
      rawPackageCode: auditMeta.rawPackageCode ?? null,
      resolvedPackageCode: auditMeta.resolvedPackageCode ?? null,
      hrCommissionType: m.hrCommission?.tierIndex != null ? "tier_percent" : "flat_percent",
      hrCommissionValue:
        Number.isFinite(Number(m.hrCommission?.percentApplied)) &&
        Number(m.hrCommission?.percentApplied) >= 0
          ? Number(m.hrCommission.percentApplied)
          : null,
      apiGrossUsdPerPerson,
      netUsdAfterSupplierCommission,
      hrCommissionAmountUsd,
      finalClientUsdPerPerson,
      missingPackage: Boolean(auditMeta.missingPackage),
      fallbackUsed: Boolean(auditMeta.fallbackUsed),
      sourceFunction: "runLdsVacationPackageFareFromEngine",
    });
  }
  return {
    pricingStatus: "ok",
    ourPrice,
    origCurrencyVm,
    netForeign: meta.netForeign,
    auditSteps,
  };
}

/**
 * עמלת טיפול + אשראי על ourFareDetails (אחרי מנוע LDS).
 */
export function applyLdsHandlingFeeAndCardToFareDetails({
  ourFareDetails,
  numberOfRooms,
  handleFeeData,
  getCreditCardFeePercent,
  fixedFlightCommission = 0,
  /** ברירת מחדל: אין חלופה — מחיצה דורסנית על handleFee value לכל חדר (₪); יש למסור בעת ויתור בלבד */
  perRoomFixedMarkupIls,
}) {
  const auditSteps = ["lds_handling:enter"];
  if (!ourFareDetails || !ourFareDetails.ourTotalFare) {
    auditSteps.push("reject:missing_ourTotalFare");
    return { ourFareDetails, auditSteps };
  }

  const updatedFareDetails = JSON.parse(JSON.stringify(ourFareDetails));
  const ourTotalFare = updatedFareDetails.ourTotalFare;

  let priceWithHandleFee = ourTotalFare.priceWithCommission || 0;
  const fixedFlightCommissionNum = Math.max(
    0,
    Number.isFinite(Number(fixedFlightCommission))
      ? Number(fixedFlightCommission)
      : 0
  );
  if (fixedFlightCommissionNum > 0) {
    const existingAgentCommission = Number(ourTotalFare.agentCommission) || 0;
    ourTotalFare.fixedFlightCommission = fixedFlightCommissionNum;
    ourTotalFare.agentCommission = legacyAppRound2(
      existingAgentCommission + fixedFlightCommissionNum
    );
    priceWithHandleFee = legacyAppRound2(priceWithHandleFee + fixedFlightCommissionNum);
    ourTotalFare.priceWithCommission = priceWithHandleFee;
    auditSteps.push(`fixed_flight_commission:+${fixedFlightCommissionNum}`);
  }

  if (handleFeeData && priceWithHandleFee > 0) {
    if (handleFeeData.type === "%") {
      const handleFee = legacyAppRound2(
        (priceWithHandleFee * handleFeeData.value) / 100
      );
      ourTotalFare.handleFee = handleFee;
      priceWithHandleFee = legacyAppRound2(priceWithHandleFee + handleFee);
    } else if (handleFeeData.type === "+") {
      const rooms = Math.max(1, Number(numberOfRooms) || 1);
      const markupOverride =
        perRoomFixedMarkupIls !== undefined && perRoomFixedMarkupIls !== null
          ? Number(perRoomFixedMarkupIls)
          : NaN;
      const handleFee = Number.isFinite(markupOverride)
        ? legacyAppRound2(markupOverride)
        : legacyAppRound2(handleFeeData.value / rooms);
      ourTotalFare.handleFee = handleFee;
      priceWithHandleFee = legacyAppRound2(priceWithHandleFee + handleFee);
    } else {
      ourTotalFare.handleFee = ourTotalFare.handleFee || 0;
    }
  } else {
    ourTotalFare.handleFee = ourTotalFare.handleFee || 0;
  }

  ourTotalFare.priceWithHandleFee = priceWithHandleFee;

  const ccPct =
    typeof getCreditCardFeePercent === "function"
      ? getCreditCardFeePercent()
      : 0;
  if (typeof getCreditCardFeePercent !== "function") {
    auditSteps.push("warn:missing_getCreditCardFeePercent");
  }

  const clearing = runPaymentCreditClearingOnBase({
    baseForFeeIls: priceWithHandleFee,
    feePercent: ccPct,
    roundMoney: legacyAppRound2,
  });
  ourTotalFare.creditCardFee = clearing.creditCardFee ?? 0;
  ourTotalFare.priceWithCommissionAndCreditCardFee = legacyAppRound2(
    priceWithHandleFee + (clearing.creditCardFee ?? 0)
  );
  auditSteps.push(...(clearing.auditSteps ?? []));

  const prev = Array.isArray(ourTotalFare.pricingAuditSteps)
    ? ourTotalFare.pricingAuditSteps
    : [];
  ourTotalFare.pricingAuditSteps = [...prev, ...auditSteps, "lds_handling:ok"];

  auditSteps.push("lds_handling:ok");
  return { ourFareDetails: updatedFareDetails, auditSteps };
}
